import {
    CSSProperties,
    ReactElement,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react';
import styles from './common.module.scss';
import { UIAvatar } from '../ui-avatar';
import { UIIcon } from '../ui-icon';
import { UIMenu, UIMenuItem } from '../ui-menu';
import { UIPopover, UIPopoverMountSensor, UIPropoverRef } from '../ui-popover';
import { UISearchInput } from '../ui-search-input';
import { UIErrorState, UILoadingState } from '../ui-state';
import { UIText } from '../ui-text';
import { charcoal, concrete } from '../../styles';
import { UISelectDropDownZIndex } from '../z-index';
import { useInfiniteScrollSentinel } from '../../hooks';
import { UIPaginatedSelectOption } from './ui-paginated-select-input';

export type UIPaginatedMultiSelectInputProps = {
    // Form-field chrome, mirroring UISelectInput.
    label?: string;
    description?: string;
    placeholder: string;
    required?: boolean;
    disabled?: boolean;
    errorText?: string;
    menuWidth?: number;
    style?: CSSProperties;

    /**
     * The current selection. Supply `label` for values that are not (yet) in
     * the loaded window — the component caches every label it has seen.
     */
    value: UIPaginatedSelectOption[];
    onChange: (options: UIPaginatedSelectOption[]) => void;

    // The loaded pages, in server order.
    options: UIPaginatedSelectOption[];
    search: string;
    onSearchChange: (query: string) => void;
    searchPlaceholder: string;
    emptyText: string;
    errorStateText: string;
    isLoading: boolean;
    isError: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    onRetry?: () => void;
    /** Fired when the dropdown opens and closes, so callers can latch fetching. */
    onOpenChanged?: (open: boolean) => void;
};

/**
 * The multi-select sibling of `UIPaginatedSelectInput`: options arrive in pages
 * with server-side search, so the component never holds the full list. Picking
 * a row toggles it and leaves the menu open, and every option ever seen is
 * cached so selections made on an earlier page keep their labels.
 */
export function UIPaginatedMultiSelectInput(
    props: UIPaginatedMultiSelectInputProps
): ReactElement {
    const {
        label,
        description,
        placeholder,
        required = false,
        disabled = false,
        errorText = '',
        menuWidth = 360,
        style,
        value,
        onChange,
        options,
        search,
        onSearchChange,
        searchPlaceholder,
        emptyText,
        errorStateText,
        isLoading,
        isError,
        isFetchingNextPage,
        hasNextPage,
        onLoadMore,
        onRetry,
        onOpenChanged
    } = props;

    const id = useId();
    const popoverRef = useRef<UIPropoverRef>(null);

    // The search input inside the menu takes focus once it opens, so the
    // trigger's own :focus can't carry the active styling on its own.
    const [isOpen, setIsOpen] = useState(false);

    // Every option ever received, plus whatever the caller supplied, so an
    // off-window selection keeps its label.
    const metaRef = useRef(new Map<string, UIPaginatedSelectOption>());
    useEffect(() => {
        options.forEach((option) => metaRef.current.set(option.value, option));
        value.forEach((option) => metaRef.current.set(option.value, option));
    }, [options, value]);

    const selected = useMemo(
        () =>
            value.map((option) => metaRef.current.get(option.value) ?? option),
        [value]
    );

    const selectedValues = useMemo(
        () => new Set(selected.map((option) => option.value)),
        [selected]
    );

    // What is already picked leaves the list — it is shown as a pill in the
    // field, which is also where it gets removed.
    const listOptions = useMemo(
        () => options.filter((option) => !selectedValues.has(option.value)),
        [options, selectedValues]
    );

    const { ref: bottomRef } = useInfiniteScrollSentinel({
        hasNextPage,
        isFetchingNextPage,
        onLoadMore
    });

    // Picking does not close the popover — choosing several in a row is the
    // whole point of a multi-select.
    function add(option: UIPaginatedSelectOption) {
        onChange([...selected, option]);
    }

    function remove(option: UIPaginatedSelectOption) {
        onChange(selected.filter((current) => current.value !== option.value));
    }

    // Only meaningful once there are no further pages: selecting everything on
    // an early page empties the list while more are still to come, and the
    // sentinel below fetches them.
    const showEmpty =
        !isLoading && !isError && listOptions.length === 0 && !hasNextPage;

    const triggerClassNames = [styles.paginatedSelectTrigger];
    if (errorText) {
        triggerClassNames.push(styles.paginatedSelectTriggerError);
    }
    if (isOpen) {
        triggerClassNames.push(styles.paginatedSelectTriggerActive);
    }
    if (disabled) {
        triggerClassNames.push(styles.paginatedSelectTriggerDisabled);
    }

    return (
        <div className={styles.container} style={style}>
            {label && (
                <div className={styles.labelContainer}>
                    <div>
                        <label className={styles.label} htmlFor={id}>
                            {label}
                        </label>
                        {required && (
                            <label
                                className={`${styles.label} ${styles.labelRequired}`}
                                htmlFor={id}
                            >
                                {' *'}
                            </label>
                        )}
                    </div>
                </div>
            )}

            <UIPopover
                ref={popoverRef}
                width={menuWidth}
                popoverPadding={4}
                placement="bottom_right"
                // The default matches UIModalZIndex, and this input is used
                // inside modals.
                zIndex={UISelectDropDownZIndex}
                triggerNode={
                    // A div rather than a button: each pill carries its own
                    // remove button, and a button inside a button is invalid
                    // HTML. UIPopover clones this and attaches the onClick, so
                    // Enter/Space are forwarded to it by hand. Clicks are
                    // suppressed while disabled via pointer-events.
                    <div
                        id={id}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-disabled={disabled || undefined}
                        className={triggerClassNames.join(' ')}
                        onKeyDown={(e) => {
                            // A focused pill handles its own keys.
                            if (e.target !== e.currentTarget) {
                                return;
                            }
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                            }
                        }}
                    >
                        <span className={styles.paginatedSelectTriggerLabel}>
                            {selected.length === 0 ? (
                                <UIText type="default2">{placeholder}</UIText>
                            ) : (
                                selected.map((option) => (
                                    <span
                                        key={option.value}
                                        className={styles.paginatedSelectPill}
                                    >
                                        {option.image !== undefined && (
                                            <UIAvatar
                                                img={option.image}
                                                alt={option.label}
                                                size={20}
                                            />
                                        )}
                                        <span
                                            className={
                                                styles.paginatedSelectPillLabel
                                            }
                                        >
                                            <UIText variant="small">
                                                {option.label}
                                            </UIText>
                                        </span>
                                        <button
                                            type="button"
                                            disabled={disabled}
                                            className={
                                                styles.paginatedSelectPillRemove
                                            }
                                            onClick={(e) => {
                                                // Otherwise the click reaches
                                                // the trigger and opens the
                                                // menu.
                                                e.stopPropagation();
                                                remove(option);
                                            }}
                                        >
                                            <UIIcon
                                                name="x"
                                                size={14}
                                                color={charcoal}
                                            />
                                        </button>
                                    </span>
                                ))
                            )}
                        </span>
                        {/* Fixed width in both states: the clear button keeps
                            its space when there is nothing to clear, so the
                            pills never reflow as the last one goes. */}
                        <span className={styles.paginatedSelectIndicators}>
                            <button
                                type="button"
                                disabled={disabled}
                                aria-hidden={selected.length === 0}
                                className={[
                                    styles.paginatedSelectIndicator,
                                    styles.paginatedSelectClear,
                                    selected.length === 0
                                        ? styles.paginatedSelectClearHidden
                                        : ''
                                ].join(' ')}
                                onClick={(e) => {
                                    // Otherwise the click reaches the trigger
                                    // and opens the menu.
                                    e.stopPropagation();
                                    onChange([]);
                                }}
                            >
                                <UIIcon name="x" size={16} color={concrete} />
                            </button>
                            <span className={styles.paginatedSelectDivider} />
                            <span className={styles.paginatedSelectIndicator}>
                                <UIIcon
                                    name="chevron-down"
                                    size={16}
                                    color={concrete}
                                />
                            </span>
                        </span>
                    </div>
                }
                onClickOutside={() => {
                    popoverRef.current?.close();
                }}
            >
                <UIPopoverMountSensor
                    // Reset on open, not on close: resetting on close changes
                    // the query key after the menu is gone and refetches the
                    // first page for nothing.
                    onMount={() => {
                        onSearchChange('');
                        setIsOpen(true);
                        onOpenChanged?.(true);
                    }}
                    onUnmount={() => {
                        setIsOpen(false);
                        onOpenChanged?.(false);
                    }}
                />
                <UIMenu
                    scroll
                    maxHeight={320}
                    header={
                        <UISearchInput
                            value={search}
                            placeholder={searchPlaceholder}
                            onChange={onSearchChange}
                        />
                    }
                >
                    {isLoading && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UILoadingState />
                        </div>
                    )}
                    {!isLoading && isError && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UIErrorState
                                title={errorStateText}
                                onRetry={onRetry}
                            />
                        </div>
                    )}
                    {showEmpty && (
                        <div className={styles.paginatedSelectStateContainer}>
                            <UIText type="default2">{emptyText}</UIText>
                        </div>
                    )}
                    {!isLoading &&
                        !isError &&
                        listOptions.map((option) => (
                            <UIMenuItem
                                key={option.value}
                                text={option.label}
                                description={option.subLabel}
                                leftAvatarProps={
                                    option.image !== undefined
                                        ? {
                                              img: option.image,
                                              alt: option.label,
                                              size: 24
                                          }
                                        : undefined
                                }
                                disabled={option.disabled}
                                onClick={() => add(option)}
                            />
                        ))}
                    {!isLoading && !isError && hasNextPage && (
                        <div
                            ref={bottomRef}
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: 8
                            }}
                        >
                            {isFetchingNextPage && <UILoadingState text="" />}
                        </div>
                    )}
                </UIMenu>
            </UIPopover>

            {errorText && (
                <div className={styles.errorTextContainer}>
                    <UIText variant="small" type="error">
                        {errorText}
                    </UIText>
                </div>
            )}
            {!errorText && description && (
                <div className={styles.descriptionTextContainer}>
                    <UIText variant="small" type="default2">
                        {description}
                    </UIText>
                </div>
            )}
        </div>
    );
}
